// Seasonings picker — the per-recipe override step in the "Add ingredients"
// flow. After the user picks a shopping list, if the recipe calls for any
// spice-rack seasonings we show them here as a checklist instead of dumping
// them in the cart.
//
// Default checked state honors the household's spice rack:
//   • rack configured + you DON'T stock it  → pre-checked (you lack it, buy)
//   • rack configured + you stock it          → unchecked ("in your rack")
//   • rack NOT configured yet                 → unchecked (assume you have basics)
// The user can override any of these before confirming.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import type { RecipeSeasoning } from "../../lib/rpc";

interface SeasoningsPickerProps {
  open: boolean;
  listName: string;
  seasonings: RecipeSeasoning[];
  /** Whether the household has set up its spice rack — drives defaults. */
  configured: boolean;
  onClose: () => void;
  /** Confirm with the canonical names the user chose to add. */
  onConfirm: (includeSeasonings: string[]) => void;
}

export function SeasoningsPicker({
  open, listName, seasonings, configured, onClose, onConfirm,
}: SeasoningsPickerProps) {
  // Default-checked = the ones you lack (only once the rack is configured).
  const defaults = useMemo(() => {
    const s = new Set<string>();
    if (configured) for (const x of seasonings) if (!x.stocked) s.add(x.name);
    return s;
  }, [seasonings, configured]);

  const [checked, setChecked] = useState<Set<string>>(defaults);
  useEffect(() => { if (open) setChecked(new Set(defaults)); }, [open, defaults]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const toggle = (name: string) =>
    setChecked((prev) => {
      const n = new Set(prev);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });

  return (
    <div className="list-picker-bg" onClick={onClose}>
      <div className="seasonings-picker" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Seasonings to add">
        <header className="list-picker-head">
          <span className="list-picker-title">Staples — add any you're out of?</span>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={14} />
          </button>
        </header>

        <p className="seasonings-picker-hint">
          {configured
            ? "Pre-checked are the ones you don't keep stocked. Adjust as needed."
            : "These stay off your list unless you check them. Set up your staples in Pantry to pre-fill this."}
        </p>

        <div className="seasonings-picker-rows">
          {seasonings.map((s) => {
            const on = checked.has(s.name);
            return (
              <button
                key={s.name}
                type="button"
                className="seasonings-picker-row"
                data-on={on}
                onClick={() => toggle(s.name)}
                aria-pressed={on}
              >
                <span className="seasonings-picker-check" aria-hidden>
                  {on && <Icon name="check" size={13} />}
                </span>
                <span className="seasonings-picker-name">{s.name}</span>
                {s.quantity && <span className="seasonings-picker-qty">{s.quantity}</span>}
                {s.stocked && <span className="seasonings-picker-badge">stocked</span>}
              </button>
            );
          })}
        </div>

        <div className="list-picker-actions">
          <button className="btn-ghost" onClick={() => onConfirm([])} type="button">
            Skip these
          </button>
          <button className="btn btn-primary" onClick={() => onConfirm([...checked])} type="button">
            <Icon name="plus" size={13} />
            <span>
              Add to {listName}
              {checked.size > 0 ? ` · +${checked.size}` : ""}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
